<script lang="ts">
import { computed, useTemplateRef } from 'vue';

import { PhxsgButton } from '@sg/src/View/Components/Atoms/Button';
import type { PhxsgIconIds, PhxsgIconSize } from '@sg/src/View/Components/Atoms/Icon';
import {
  PhxsgSlider as PhxsgCoreSlider,
  type PhxsgSliderProps as PhxsgCoreSliderProps,
} from '@sg-core/src/View/Components/Organisms/Slider';

import type { Swiper as SwiperType } from 'swiper/types';

export type PhxsgSliderControlsLayout = 'inner' | 'outer' | 'split';

export interface PhxsgSliderNavIcon {
  id?: PhxsgIconIds;
  size?: PhxsgIconSize;
  bidirectional?: boolean;
}

export interface PhxsgSliderProps extends PhxsgCoreSliderProps {
  controlsLayout?: PhxsgSliderControlsLayout;
  navPrevIcon?: PhxsgIconIds | PhxsgSliderNavIcon;
  navNextIcon?: PhxsgIconIds | PhxsgSliderNavIcon;
}
</script>

<script setup lang="ts">
const {
  controlsLayout,
  overlay,
  pagination = true,
  navigation = true,
  navPrevIcon,
  navNextIcon,
  ...props
} = defineProps<PhxsgSliderProps>();

const prevIcon = computed(() => ({
  id: 'phxsg-arrow-left' as PhxsgIconIds,
  size: 'md' as PhxsgIconSize,
  bidirectional: true,
  ...(typeof navPrevIcon === 'string' ? { id: navPrevIcon } : navPrevIcon),
}));

const nextIcon = computed(() => ({
  id: 'phxsg-arrow-right' as PhxsgIconIds,
  size: 'md' as PhxsgIconSize,
  bidirectional: true,
  ...(typeof navNextIcon === 'string' ? { id: navNextIcon } : navNextIcon),
}));

const coreSliderRef = useTemplateRef('coreSliderRef');

defineExpose({
  get swiper() {
    return coreSliderRef.value?.swiper;
  },
});

defineEmits<{
  swiper: [swiper: SwiperType];
}>();
</script>

<template>
  <PhxsgCoreSlider
    v-bind="{ pagination, navigation, ...props }"
    ref="coreSliderRef"
    :pagination-position="controlsLayout === 'split' ? 'outer' : controlsLayout"
    :nav-slot="controlsLayout === 'split' ? 'container-end' : props.navSlot"
    :class="{
      [`phxsg-slider--controls-${controlsLayout}`]: !!controlsLayout,
      'phxsg-theme-blue-80': controlsLayout === 'inner',
    }"
    :overlay="overlay ?? controlsLayout === 'inner'"
    @swiper="$emit('swiper', $event)"
  >
    <slot />

    <template #nav-prev="{ label }">
      <PhxsgButton class="phxsg-slider__nav-btn" icon-only variant="ghost" :icon="{ start: prevIcon }">
        {{ label }}
      </PhxsgButton>
    </template>

    <template #nav-next="{ label }">
      <PhxsgButton class="phxsg-slider__nav-btn" icon-only variant="ghost" :icon="{ start: nextIcon }">
        {{ label }}
      </PhxsgButton>
    </template>

    <template #pagination="{ id, className, role, ariaLabel }">
      <div :id="id" :class="className" :role="role" :aria-label="ariaLabel"></div>
    </template>

    <template #additional-content>
      <slot name="additional-content" />
    </template>
  </PhxsgCoreSlider>
</template>

<style src="./slider.css" />
